import { routes } from '../../router';
import roleConstants from '../../utilities/enums/rolesTypes';
import $rq from '@/api/api';
import { User, Role, Permission } from '@/interfaces/common';
import { ActionContext } from 'vuex';
import { RouteRecordRaw } from 'vue-router';
import type { RootState } from '@/interfaces/store';
import { LOCALES } from '@/locales/i18n';
import navItems from '@/constants/navItems';

interface AccessControlState {
    isStateAccessControlLoaded: boolean;
    permissions: Permission[];
    roles: Role[];
    accessibleRoutesByPath: Record<string, AccessibleRoute>;
    accessibleRoutesByName: Record<string, AccessibleRoute>;
    displayNote: boolean;
    termsOfUseAccepted: boolean;
    superAdmin: boolean;
}

interface AccessibleRoute {
    alias: string | string[] | undefined;
    meta: {
        everyNeededPermissions: string[];
        atLeastNeededRoles: Role[];
    };
    name: string;
    path: string;
}

const defaultState = () => ({
    // to check if state Access Control is loaded
    isStateAccessControlLoaded: false,
    // Merged permissions of all the different roles of the current user related to the current selected team
    permissions: [],
    // Roles list of the current user related to the current selected team
    roles: [],
    // Map with all the routes accessible for the user based on his role/permissions, key are route path here, value is route object
    accessibleRoutesByPath: {},
    // Map with all the routes accessible for the user based on his role/permissions, key are component name and value route object
    accessibleRoutesByName: {},
    // to check if we display the release note or not and terms or not
    displayNote: false,
    // to check if we display the terms or not
    termsOfUseAccepted: true,
    // to check if user is superAdmin
    superAdmin: false,
});

const mutations = {
    setIsStateAccessControlLoaded(state: AccessControlState, isStateAccessControlLoaded: boolean) {
        state.isStateAccessControlLoaded = isStateAccessControlLoaded;
    },

    setPermissions(state: AccessControlState, { permissions }: { permissions: Permission[] }) {
        state.permissions = permissions;
    },
    setRoles(state: AccessControlState, { roles }: { roles: Role[] }) {
        state.roles = roles;
    },
    setTermsOfUseAccepted(state: AccessControlState, termsOfUseAccepted: boolean) {
        state.termsOfUseAccepted = termsOfUseAccepted;
    },
    setDisplayNote(state: AccessControlState, displayNote: boolean) {
        state.displayNote = displayNote;
    },
    setSuperAdmin(state: AccessControlState, superAdmin: boolean) {
        state.superAdmin = superAdmin;
    },
    setAccessibleRoutesByPath(
        state: AccessControlState,
        { accessibleRoutesByPath }: Record<string, AccessibleRoute>,
    ) {
        state.accessibleRoutesByPath = JSON.parse(JSON.stringify(accessibleRoutesByPath));
    },
    setAccessibleRoutesByName(
        state: AccessControlState,
        { accessibleRoutesByName }: Record<string, AccessibleRoute>,
    ) {
        state.accessibleRoutesByName = JSON.parse(JSON.stringify(accessibleRoutesByName));
    },
    resetAccessControlState(state: AccessControlState) {
        Object.assign(state, defaultState());
    },
};

// HELPER FUNCTIONS
const buildPermissionsWithoutDuplicates = (roles: Role[]): Permission[] => {
    const mergedPermissionsWithDuplicates: Permission[] = roles
        .map((role) => role.permissions)
        .flat();

    const uniquePermissions = new Map<string, Permission>();
    mergedPermissionsWithDuplicates.forEach((p) => {
        if (p && p.key && !uniquePermissions.has(p.key)) {
            uniquePermissions.set(p.key, p);
        }
    });

    return Array.from(uniquePermissions.values());
};

type BuildAccessibleRoutesContext = {
    getters: {
        hasEveryPermissions: (perms: string[]) => boolean;
        hasAtLeastOneRole: (roles: Role[]) => boolean;
    };
};

const buildAccessibleRoutes = (
    routes: Array<RouteRecordRaw>,
    context: BuildAccessibleRoutesContext,
): AccessibleRoute[] => {
    const childrenRoutesWithParentRoutes = routes.reduce<AccessibleRoute[]>((output, route) => {
        if (Array.isArray(route.children)) {
            output.push(
                ...route.children.map((child: RouteRecordRaw) => ({
                    alias: child.alias,
                    meta: {
                        ...child.meta,
                        everyNeededPermissions: Array.isArray(child.meta?.everyNeededPermissions)
                            ? (child.meta?.everyNeededPermissions as string[])
                            : [],
                        atLeastNeededRoles: Array.isArray(child.meta?.atLeastNeededRoles)
                            ? (child.meta?.atLeastNeededRoles as Role[])
                            : [],
                    },
                    name: child.name as string,
                    path: `${route.path}/${child.path}`,
                })),
            );
        }

        output.push({
            alias: route.alias,
            meta: {
                ...route.meta,
                everyNeededPermissions: Array.isArray(route.meta?.everyNeededPermissions)
                    ? (route.meta?.everyNeededPermissions as string[])
                    : [],
                atLeastNeededRoles: Array.isArray(route.meta?.atLeastNeededRoles)
                    ? (route.meta?.atLeastNeededRoles as Role[])
                    : [],
            },
            name: route.name as string,
            path: route.path,
        });

        return output;
    }, []);

    return childrenRoutesWithParentRoutes.filter((route) => {
        if (route.path === '*') return false;
        if (!route.meta) return true;
        if (
            route.meta.everyNeededPermissions &&
            !context.getters.hasEveryPermissions(route.meta.everyNeededPermissions)
        )
            return false;
        return !(
            route.meta.atLeastNeededRoles &&
            !context.getters.hasAtLeastOneRole(route.meta.atLeastNeededRoles)
        );
    });
};

const actions = {
    async setAccessControlInfo(context: ActionContext<AccessControlState, RootState>) {
        try {
            const response = await $rq.getMemberInfo('me');
            const { roles, superAdmin, termsOfUseAccepted, displayNote } = response;

            // Mutation in auth module - pass full response to get all user info
            context.commit('setCurrentUser', response);
            context.commit('setSuperAdmin', superAdmin);

            // Mutation in alertsNotification
            // TODO: refactor the alertNotification par with socket. remove this interval concept.
            // context.commit('alertsNotification/startInterval', undefined, { root: true });
            context.commit({
                type: 'setTermsOfUseAccepted',
                termsOfUseAccepted: termsOfUseAccepted, // from destructured currentUser
            });

            context.commit({
                type: 'setDisplayNote',
                displayNote: displayNote?.web || false, // from destructured currentUser, using 'web' property
            });

            const _roles: Record<string, Role> = roles.reduce(
                (o: Record<string, Role>, role: Role) =>
                    Object.assign(o, {
                        [role.key.split('.').join('_')]: role,
                    }),
                {},
            );

            context.commit({
                type: 'setRoles',
                roles: Object.values(_roles),
            });

            context.commit('setLang', context.rootState.auth.user.lang || LOCALES[0], {
                root: true,
            });

            context.commit({
                type: 'setPermissions',
                permissions: buildPermissionsWithoutDuplicates(roles),
            });
            const accessibleRoutes = buildAccessibleRoutes(Object.assign([], routes), context);
            context.commit({
                type: 'setAccessibleRoutesByPath',
                accessibleRoutesByPath: JSON.parse(JSON.stringify(accessibleRoutes))
                    .filter((route: AccessibleRoute) => route.path)
                    .reduce((o: Record<string, AccessibleRoute>, route: AccessibleRoute) => {
                        o[route.path.split('.').join('_')] = route;
                        return o;
                    }, {}),
            });
            context.commit({
                type: 'setAccessibleRoutesByName',
                accessibleRoutesByName: JSON.parse(JSON.stringify(accessibleRoutes))
                    .filter((route: AccessibleRoute) => route.name)
                    .reduce((o: Record<string, AccessibleRoute>, route: AccessibleRoute) => {
                        o[route.name.split('.').join('_')] = route;
                        return o;
                    }, {}),
            });
            context.commit({
                type: 'setIsStateAccessControlLoaded',
                isStateAccessControlLoaded: true,
            });
        } catch (err) {
            console.error('Failed to load access control:', err);
            context.dispatch('resetAccessControlState');
            context.dispatch('refreshUserTeams', { refreshToDefaultRoute: true }, { root: true });
        }
    },
    resetAccessControlState({ commit }: ActionContext<AccessControlState, RootState>) {
        commit({
            type: 'resetAccessControlState',
        });
    },
};

const getters = {
    isStateAccessControlLoaded: (state: AccessControlState) => state.isStateAccessControlLoaded,
    roles: (state: AccessControlState) => state.roles,
    superAdmin: (state: AccessControlState) => state.superAdmin,
    displayNote: (state: AccessControlState) => state.displayNote,
    termsOfUseAccepted: (state: AccessControlState) => state.termsOfUseAccepted,
    permissions: (state: AccessControlState) => state.permissions,
    hasPermission: (state: AccessControlState) => (permission: string) => {
        const perms = Array.isArray(state.permissions) ? state.permissions : [];
        const canAccess = perms.some((p: Permission) => {
            const part = p && p.key ? p.key.split('*')[0] : null;
            return part && permission.startsWith(part);
        });
        return canAccess;
    },
    hasRole: (state: AccessControlState) => (role: string) => {
        const _hasRole = state.roles.find((r: Role) => r.key === role) !== undefined;
        return _hasRole;
    },
    defaultUserRoute: (
        state: AccessControlState,
        getters: { hasRole: (role: string) => boolean },
    ): string => {
        const section = navItems.find((nav) => nav.roles.some((role) => getters.hasRole(role)));
        return section ? `/${section.name}` : '/clients';
    },
    hasAtLeastOneRole:
        (state: AccessControlState, getters: { hasRole: (role: string) => boolean }) =>
        (roles: Role[]) => {
            if (!Array.isArray(roles)) {
                return false;
            }
            return roles.some((role: Role) => getters.hasRole(role.key));
        },
    hasEveryPermissions:
        (state: AccessControlState, getters: { hasPermission: (permission: string) => boolean }) =>
        (permissions: string[]) => {
            if (!Array.isArray(permissions)) {
                return false;
            }
            return permissions.every((permission) => getters.hasPermission(permission));
        },
    hasAccessToRoute(state: AccessControlState) {
        return (route: { name: string } | string) => {
            if (typeof route !== 'string' && route.name === 'login') return true;
            if (route === '/' || route === 'login') return true;
            return typeof route === 'string'
                ? state.accessibleRoutesByPath[route.split('.').join('_')] !== undefined ||
                      state.accessibleRoutesByName[route.split('.').join('_')] !== undefined
                : state.accessibleRoutesByPath[route.name.split('.').join('_')] !== undefined ||
                      state.accessibleRoutesByName[route.name.split('.').join('_')] !== undefined;
        };
    },
    isAdminOrManager() {
        return (user: User) => {
            const adminsAndManagers = [
                roleConstants.ADMINISTRATOR,
                roleConstants.MANAGER,
                roleConstants.LABMANAGER,
                roleConstants.LABMICROBIOLOGIST,
            ];
            return user.roles.find((role: Role) =>
                adminsAndManagers.includes(role.key as (typeof adminsAndManagers)[number]),
            );
        };
    },
};

const state = defaultState();

export default {
    state,
    getters,
    actions,
    mutations,
};
